import { getRootUrl } from '@share-a-cart/shared-utils';

/** Forwarder paths that accept a trailing :source? segment (/u, /uv, /uvv). */
const FORWARDER_PATH = /^\/(u|uv|uvv)\//;

/** Sources any of our clients emit, so an already-tagged link is left alone. */
const SOURCE_SEGMENT = /\/(web|mcp|classroom|ext-[a-z0-9_]+-[a-z0-9_]+|mobile-(?:android|ios))$/;

export const isForwarderPath = (path: string): boolean => FORWARDER_PATH.test(path);

/**
 * Tags a forwarder link with the source that activation.source records. Applied where the link is
 * rendered rather than where it is built, so the server keeps returning one cache-safe link per
 * cart instead of a per-client one.
 *
 * Idempotent, and a no-op on anything that is not a forwarder path: a fourth path segment stops
 * matching /u/:loc/:source?, and appending to a retailer URL would corrupt it.
 */
export const withSource = (path: string, source = 'web'): string => {
  if (!path || !FORWARDER_PATH.test(path) || SOURCE_SEGMENT.test(path)) {
    return path;
  }

  return `${path}/${source}`;
};

/**
 * Outbound href for a cart item link. Only prefix getRootUrl() for /u|/uv|/uvv forwarder paths.
 * Never turn a bare retailer path like `/products/...?variant=` into share-a-cart.com/products/...
 */
export const itemOutboundHref = (url: string | undefined, source = 'web'): string => {
  if (!url) {
    return '';
  }
  if (isForwarderPath(url)) {
    return `${getRootUrl()}${withSource(url, source)}`;
  }
  if (/^https?:\/\//i.test(url)) {
    return url;
  }
  // Relative non-forwarder paths must not be absolutized against SAC.
  return '';
};

/**
 * A ?source= query value handed to a page by the extension or mobile app. Same charset as
 * SOURCE_SEGMENT: ua-parser-js browser names carry underscores (mobile_chrome, samsung_internet).
 */
const SOURCE_PARAM = /^(web|ext-[a-z0-9_]+-[a-z0-9_]+|mobile-(?:android|ios))$/;

/** Returns the source to record for a page's ?source= value, or 'web' for anything unrecognized. */
export const sanitizeSourceParam = (raw: string | null | undefined): string =>
  raw && SOURCE_PARAM.test(raw) ? raw : 'web';
